import zeusSdk from '_/utils/SDKs';

export interface IAlarm {
  id: string;
  name: string;
  alarmGroupId: string;
  addedAt: string;
  active: boolean;
  severity: string;
  metric: string;
  threshold: number;
  comparisonOperator: string;
  readme?: {
    content: string;
  };
}

export interface ICreateAlarmDTO {
  name: string;
  groupId: string;
  active: boolean;
  severity: string;
  metric: string;
  threshold: number;
  comparisonOperator: string;
}

export interface IUpdateAlarmDTO {
  name?: string;
  active?: boolean;
  severity?: string;
  metric?: string;
  threshold?: number;
  comparisonOperator?: string;
}

export interface IApiRequestOptions {
  queryParameters?: Record<string, any>;
  include?: string;
}

/**
 * Fetch all alarms for an alarm group
 */
export const requestAlarms = async (alarmGroupId: string, params: IApiRequestOptions = {}): Promise<IAlarm[]> => {
  const options = {
    queryParameters: {
      sort: 'name',
      sortDirection: 'ASC',
      ...params.queryParameters,
    },
    include: 'readme',
  };

  const response = await zeusSdk.alarmGroups.getChildren(alarmGroupId, 'alarms', options);
  return response.items;
};

/**
 * Fetch a single alarm by ID
 */
export const requestAlarm = async (alarmId: string, params: IApiRequestOptions = {}): Promise<IAlarm> => {
  return zeusSdk.alarms.get(alarmId, { include: 'readme', ...params });
};

/**
 * Create a new alarm
 */
export const createAlarmRequest = async (alarm: ICreateAlarmDTO, readme?: string): Promise<IAlarm> => {
  const newAlarm = await zeusSdk.alarms.create(alarm);

  if (readme) {
    await zeusSdk.alarms.createChild(newAlarm.id, 'readme', { content: readme });
  }

  return newAlarm;
};

/**
 * Update an alarm
 */
export const updateAlarmRequest = async (alarmId: string, alarm: IUpdateAlarmDTO): Promise<IAlarm> => {
  return zeusSdk.alarms.patch(alarmId, alarm);
};

/**
 * Update alarm readme
 */
export const updateAlarmReadmeRequest = async (alarmId: string, readme: string): Promise<void> => {
  await zeusSdk.alarms.updateChild(alarmId, 'readme', { content: readme });
};

/**
 * Delete an alarm
 */
export const deleteAlarmRequest = async (alarmId: string): Promise<void> => {
  await zeusSdk.alarms.delete(alarmId);
};
